import { useTranslation } from "react-i18next"
import { useFragment } from "react-relay"
import { Tag } from "@/components/ui/tag"
import { HealthDataCardSession } from "@/modules/patients/detail/health-information"
import { useGetHealthInformationVerification } from "@/modules/patients/detail/health-information/hooks/useGetHealthInformationVerification"
import { useHealthInformationInContextAppointment } from "@/modules/patients/detail/health-information/hooks/useHealthInformationCanVerify"
import { SocialHistoryDrawer } from "@/modules/patients/detail/health-information/social-history/social-history-drawer"
import { Country } from "@/modules/patients/services/__generated__/PatientFragment.graphql"
import { SocialHistoryFragment$key } from "@/modules/patients/services/__generated__/SocialHistoryFragment.graphql"
import { SocialHistoryFragment } from "@/modules/patients/services/SocialHistoryFragment"
import { usePatientSocialHistory } from "@/modules/patients/services/usePatientSocialHistory"
import { useDrawerStore } from "@/stores/useDrawerStore"

interface SocialHistoryBodyProps extends React.HTMLAttributes<HTMLDivElement> {
  country: Country
  patientId: string
}

const SocialHistory = ({ country, patientId }: SocialHistoryBodyProps) => {
  const dataRef = usePatientSocialHistory(patientId, country)
  const { comment, smokingHabit, drinkingHabit } = useFragment<SocialHistoryFragment$key>(
    SocialHistoryFragment,
    dataRef,
  )
  const { t } = useTranslation()
  const openDrawer = useDrawerStore((state) => state.openDrawer)
  const drawerName = useDrawerStore((state) => state.drawerName)
  const { verifiedBy, verifiedAt, isVerified } = useGetHealthInformationVerification(
    patientId,
    country,
    "SOCIAL_HISTORY",
  )

  const validAppointment = useHealthInformationInContextAppointment()
  const canVerify = !isVerified && validAppointment

  const renderSmokingHabitTag = () => {
    switch (smokingHabit) {
      case "REGULARLY":
      case "IN_COMPANY":
      case "NO_LONGER":
      case "NEVER":
        return (
          <Tag
            className="truncate"
            type="neutral"
            label={t(`patient.healthInformation.socialHistory.smokingHabit.tags.${smokingHabit}`)}
          />
        )
      case "NOT_SPECIFIED":
        return undefined
    }
  }

  const renderDrinkingHabitTag = () => {
    switch (drinkingHabit) {
      case "DAILY":
      case "FOUR_TO_FIVE_PER_WEEK":
      case "ONE_TO_THREE_PER_WEEK":
      case "LESS_THAN_ONE_TIMES_PER_WEEK":
      case "NO_LONGER":
      case "NEVER":
        return (
          <Tag
            className="truncate"
            type="neutral"
            label={t(`patient.healthInformation.socialHistory.drinkingHabit.tags.${drinkingHabit}`)}
          />
        )
      case "NOT_SPECIFIED":
        return undefined
    }
  }

  const showTag = () => {
    if (!renderDrinkingHabitTag() && !renderSmokingHabitTag() && !comment) {
      return false
    }
    return true
  }

  return (
    <HealthDataCardSession
      canVerify={canVerify}
      sectionName="social-history"
      analyticsSectionName="healthInformation_social"
      disabled={drawerName === "SocialHistoryDrawer"}
      title={t("patient.healthInformation.socialHistory.title")}
      editHandler={() => {
        openDrawer(
          () => (
            <SocialHistoryDrawer canVerify={canVerify} verifiedBy={verifiedBy ?? ""} verifiedAt={verifiedAt ?? ""} />
          ),
          "SocialHistoryDrawer",
        )
      }}>
      {showTag() && (
        <div className="flex max-w-full flex-col flex-wrap gap-2">
          <span
            data-testid="social-history-comment-value"
            className="text-on-surface-subdued text-sm leading-5 font-normal wrap-break-word break-all">
            {comment}
          </span>
          <div data-testid="social-history-habit-value" className="flex max-w-full flex-wrap items-center gap-2">
            {renderSmokingHabitTag()}
            {renderDrinkingHabitTag()}
          </div>
        </div>
      )}
    </HealthDataCardSession>
  )
}

export { SocialHistory }
